import { installMobileModal } from './modal';

export type DrawerController = { sync(open: boolean): void; destroy(): void };
export type DrawerGestureOptions = {
  drawer: HTMLElement;
  surface: HTMLElement;
  overlay: HTMLElement;
  initialOpen?: boolean;
  canStart?: () => boolean;
  canSwipeAnywhere?: () => boolean;
  onStateChange: (open: boolean) => void;
};
type Point = { x: number; y: number; time: number };
type Drag = { id: number; start: Point; last: Point; origin: boolean; claimed: boolean };
const interactive =
  'button, a, input, textarea, select, [contenteditable="true"], [role="slider"], [role="button"]';

/** Inspect every ancestor: a scroll wrapper often has no table/carousel marker. */
export function drawerExcludedTarget(
  target: Element,
  boundary: HTMLElement,
  navigation = false,
): boolean {
  const controls = navigation
    ? 'input, textarea, select, [contenteditable="true"], [role="slider"]'
    : interactive;
  if (
    target.closest(
      `${controls}, table, pre, code, [data-carousel], [data-mobile-horizontal-scroll], [data-horizontal-scroll]`,
    )
  )
    return true;
  const view = boundary.ownerDocument.defaultView!;
  for (
    let element: Element | null = target;
    element && element !== boundary;
    element = element.parentElement
  ) {
    const overflow = view.getComputedStyle(element).overflowX;
    if (element.scrollWidth > element.clientWidth + 1 && /^(auto|scroll)$/.test(overflow))
      return true;
  }
  return false;
}

export function drawerGestureIntent(dx: number, dy: number): 'wait' | 'horizontal' | 'vertical' {
  if (Math.hypot(dx, dy) < 8) return 'wait';
  return Math.abs(dy) > Math.abs(dx) * Math.tan((35 * Math.PI) / 180) ? 'vertical' : 'horizontal';
}
export function drawerReleaseOpen(
  origin: boolean,
  dx: number,
  velocity: number,
  width: number,
): boolean {
  if (origin) return !(dx <= -width * 0.28 || velocity <= -0.5);
  return dx >= width * 0.28 || velocity >= 0.5;
}

/** Only a claimed horizontal gesture suppresses native touch scrolling. No
 * ancestor touch-action restriction: tables, vertical scrolling and pinch keep
 * their native behavior. Pointer events handle mouse/pen; touch uses the narrow
 * non-passive bridge because browsers cancel pointers as native panning starts. */
export function installMobileDrawer(options: DrawerGestureOptions): DrawerController {
  const { drawer, surface, overlay, onStateChange } = options;
  const document = drawer.ownerDocument;
  const view = document.defaultView!;
  const mobile = view.matchMedia('(max-width: 640px)');
  let open = options.initialOpen ?? false;
  let drag: Drag | null = null;
  let modalCleanup: (() => void) | undefined;
  let suppressClick = false;
  const originalInert = drawer.inert;
  const originalHidden = drawer.getAttribute('aria-hidden');
  const originalRole = drawer.getAttribute('role');
  const originalModal = drawer.getAttribute('aria-modal');
  const position = (x: number, y: number, time: number): Point => ({ x, y, time });
  const progress = (value: number) => {
    const p = String(Math.max(0, Math.min(1, value)));
    drawer.style.setProperty('--mobile-drawer-progress', p);
    overlay.style.setProperty('--mobile-drawer-progress', p);
  };
  const clearDrag = () => {
    drag = null;
    drawer.removeAttribute('data-drawer-dragging');
    overlay.removeAttribute('data-drawer-dragging');
  };
  const paint = () => {
    progress(open ? 1 : 0);
    drawer.dataset.drawerOpen = String(open);
    overlay.hidden = !open;
    drawer.inert = !open;
    drawer.setAttribute('aria-hidden', String(!open));
  };
  const setOpen = (next: boolean, notify: boolean) => {
    clearDrag();
    const changed = next !== open;
    open = next;
    if (!mobile.matches) return;
    paint();
    if (open && !modalCleanup) {
      modalCleanup = installMobileModal(drawer, [surface]);
      drawer.setAttribute('role', 'dialog');
      drawer.setAttribute('aria-modal', 'true');
      const first = Array.from(
        drawer.querySelectorAll<HTMLElement>('button, a[href], [tabindex="0"]'),
      ).find((element) => element.getClientRects().length > 0);
      first?.focus({ preventScroll: true });
    } else if (!open && modalCleanup) {
      modalCleanup();
      modalCleanup = undefined;
      restore('role', originalRole);
      restore('aria-modal', originalModal);
    }
    if (notify && changed) onStateChange(open);
  };
  const cancel = () => {
    clearDrag();
    if (mobile.matches) paint();
  };
  const begin = (target: EventTarget | null, id: number, point: Point) => {
    if (
      !mobile.matches ||
      (options.canStart && !options.canStart()) ||
      !(target instanceof Element)
    )
      return;
    if (
      !open &&
      target.closest('[data-radix-popper-content-wrapper], [role=dialog], [role=alertdialog]')
    )
      return;
    const inDrawer = drawer.contains(target);
    const inOverlay = overlay.contains(target);
    if (open ? !inDrawer && !inOverlay : !surface.contains(target)) return;
    if (!inOverlay && drawerExcludedTarget(target, open ? drawer : surface, open)) return;
    if (
      !open &&
      point.x > surface.getBoundingClientRect().left + 32 &&
      options.canSwipeAnywhere &&
      !options.canSwipeAnywhere()
    )
      return;
    drag = { id, start: point, last: point, origin: open, claimed: false };
  };
  const move = (point: Point, preventDefault: () => void) => {
    if (!drag) return;
    const dx = point.x - drag.start.x;
    const dy = point.y - drag.start.y;
    if (!drag.claimed) {
      const intent = drawerGestureIntent(dx, dy);
      if (intent === 'wait') return;
      if (intent === 'vertical' || (drag.origin ? dx > 0 : dx < 0)) {
        cancel();
        return;
      }
      drag.claimed = true;
      drawer.setAttribute('data-drawer-dragging', '');
      overlay.setAttribute('data-drawer-dragging', '');
      overlay.hidden = false;
    }
    preventDefault();
    drag.last = point;
    progress((drag.origin ? 1 : 0) + dx / Math.max(1, drawer.getBoundingClientRect().width));
  };
  const finish = () => {
    if (!drag?.claimed) {
      cancel();
      return;
    }
    const { origin, start, last } = drag;
    const dx = last.x - start.x;
    const velocity = dx / Math.max(1, last.time - start.time);
    suppressClick = true;
    setOpen(drawerReleaseOpen(origin, dx, velocity, drawer.getBoundingClientRect().width), true);
  };
  const touchStart = (event: TouchEvent) => {
    suppressClick = false;
    if (event.touches.length !== 1) {
      cancel();
      return;
    }
    const touch = event.touches[0];
    begin(event.target, touch.identifier, position(touch.clientX, touch.clientY, event.timeStamp));
  };
  const touchMove = (event: TouchEvent) => {
    if (event.touches.length !== 1) {
      cancel();
      return;
    }
    const touch = event.touches[0];
    if (touch.identifier === drag?.id)
      move(position(touch.clientX, touch.clientY, event.timeStamp), () => event.preventDefault());
  };
  const touchEnd = (event: TouchEvent) => {
    if (Array.from(event.changedTouches).some((touch) => touch.identifier === drag?.id)) finish();
  };
  const pointerDown = (event: PointerEvent) => {
    if (event.pointerType === 'touch') return;
    suppressClick = false;
    if (!event.isPrimary || event.button !== 0) {
      cancel();
      return;
    }
    begin(event.target, event.pointerId, position(event.clientX, event.clientY, event.timeStamp));
  };
  const pointerMove = (event: PointerEvent) => {
    if (event.pointerType !== 'touch' && event.pointerId === drag?.id)
      move(position(event.clientX, event.clientY, event.timeStamp), () => event.preventDefault());
  };
  const pointerUp = (event: PointerEvent) => {
    if (event.pointerType !== 'touch' && event.pointerId === drag?.id) finish();
  };
  const click = (event: MouseEvent) => {
    if (!mobile.matches) return;
    if (suppressClick && event.detail !== 0) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (overlay.contains(event.target as Node)) setOpen(false, true);
  };
  const key = (event: KeyboardEvent) => {
    if (
      mobile.matches &&
      open &&
      event.key === 'Escape' &&
      !event.defaultPrevented &&
      drawer.contains(event.target as Node)
    ) {
      event.preventDefault();
      setOpen(false, true);
    }
  };
  function restore(name: string, value: string | null) {
    if (value === null) drawer.removeAttribute(name);
    else drawer.setAttribute(name, value);
  }
  const restoreDesktop = () => {
    clearDrag();
    modalCleanup?.();
    modalCleanup = undefined;
    drawer.inert = originalInert;
    restore('aria-hidden', originalHidden);
    restore('role', originalRole);
    restore('aria-modal', originalModal);
    drawer.style.removeProperty('--mobile-drawer-progress');
    overlay.style.removeProperty('--mobile-drawer-progress');
    drawer.removeAttribute('data-drawer-open');
    overlay.hidden = true;
  };
  const mode = () => {
    if (mobile.matches) setOpen(open, false);
    else {
      restoreDesktop();
      if (open) {
        open = false;
        onStateChange(false);
      }
    }
  };
  mode();
  mobile.addEventListener('change', mode);
  document.addEventListener('touchstart', touchStart, { passive: true, capture: true });
  document.addEventListener('touchmove', touchMove, { passive: false, capture: true });
  document.addEventListener('touchend', touchEnd, true);
  document.addEventListener('touchcancel', cancel, true);
  document.addEventListener('pointerdown', pointerDown);
  document.addEventListener('pointermove', pointerMove);
  document.addEventListener('pointerup', pointerUp);
  document.addEventListener('pointercancel', cancel);
  document.addEventListener('click', click, true);
  document.addEventListener('keydown', key);
  return {
    sync: (next) => setOpen(next, false),
    destroy: () => {
      restoreDesktop();
      mobile.removeEventListener('change', mode);
      document.removeEventListener('touchstart', touchStart, true);
      document.removeEventListener('touchmove', touchMove, true);
      document.removeEventListener('touchend', touchEnd, true);
      document.removeEventListener('touchcancel', cancel, true);
      document.removeEventListener('pointerdown', pointerDown);
      document.removeEventListener('pointermove', pointerMove);
      document.removeEventListener('pointerup', pointerUp);
      document.removeEventListener('pointercancel', cancel);
      document.removeEventListener('click', click, true);
      document.removeEventListener('keydown', key);
    },
  };
}
